import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  AutoAwesomeRounded,
  FilterListRounded,
} from "@mui/icons-material";
import DatesItem from "./DatesItem";
import {
  EmptyState,
  PageIntro,
  PageShell,
  SectionBlock,
  StatStrip,
} from "../ui/AppPrimitives";
import { startOfDay } from "../utils/dateUtils";

const filterSections = [
  {
    title: "View",
    options: [
      { value: "all", label: "All Celebrations" },
      { value: "important", label: "Important Only" },
    ],
  },
  {
    title: "Time",
    options: [
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
  },
  {
    title: "Category",
    options: [
      { value: "personal", label: "Personal" },
      { value: "legal", label: "Legal" },
      { value: "health", label: "Health" },
      { value: "exam", label: "Exam" },
      { value: "other", label: "Other" },
    ],
  },
];

const Celebrations = ({ dates, onDelete, onPriority }) => {
  const [filter, setFilter] = React.useState("all");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  const recurringDates = useMemo(() => {
    const today = startOfDay(new Date());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    return dates
      .filter((item) => item.type === "recurring")
      .map((item) => {
        const originalDate = new Date(item.dateTime);
        let nextOccurrence = new Date(
          today.getFullYear(),
          originalDate.getMonth(),
          originalDate.getDate()
        );

        nextOccurrence.setHours(0, 0, 0, 0);

        if (nextOccurrence < today) {
          nextOccurrence = new Date(
            today.getFullYear() + 1,
            originalDate.getMonth(),
            originalDate.getDate()
          );
        }

        if (filter === "important" && !item.priority) return null;
        if (filter === "today" && nextOccurrence.getTime() !== today.getTime()) return null;
        if (filter === "week" && (nextOccurrence <= today || nextOccurrence > endOfWeek)) return null;
        if (filter === "month" && nextOccurrence > endOfMonth) return null;

        if (["personal", "legal", "health", "exam", "other"].includes(filter)) {
          if (item.category?.toLowerCase() !== filter) return null;
        }

        return {
          ...item,
          nextDate: nextOccurrence,
          daysRemaining: Math.ceil((nextOccurrence - today) / (1000 * 60 * 60 * 24)),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.nextDate - b.nextDate);
  }, [dates, filter]);

  const grouped = useMemo(() => {
    const today = startOfDay(new Date());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    endOfYear.setHours(23, 59, 59, 999);

    const result = {
      today: [],
      week: [],
      month: [],
      year: [],
      later: [],
    };

    recurringDates.forEach((item) => {
      const itemDate = startOfDay(item.nextDate);
      if (itemDate.getTime() === today.getTime()) {
        result.today.push(item);
      } else if (itemDate <= endOfWeek) {
        result.week.push(item);
      } else if (itemDate <= endOfMonth) {
        result.month.push(item);
      } else if (itemDate <= endOfYear) {
        result.year.push(item);
      } else {
        result.later.push(item);
      }
    });

    return result;
  }, [recurringDates]);

  const filterLabel =
    filterSections
      .flatMap((section) => section.options)
      .find((option) => option.value === filter)?.label || "All Celebrations";

  const priorityCount = recurringDates.filter((item) => item.priority).length;

  const renderGroup = (label, items) => {
    if (items.length === 0) return null;

    return (
      <SectionBlock
        key={label}
        title={label}
        subtitle={`${items.length} celebration${items.length > 1 ? "s" : ""}`}
      >
        <Stack spacing={1.6}>
          {items.map((item) => (
            <DatesItem
              key={item.id}
              dateItem={{
                ...item,
                dateTime: item.nextDate.toISOString(),
              }}
              onDelete={onDelete}
              onPriority={onPriority}
              celebration
            />
          ))}
        </Stack>
      </SectionBlock>
    );
  };

  return (
    <PageShell maxWidth="xl">
      <PageIntro
        eyebrow="Celebrations"
        title="Recurring moments, surfaced beautifully."
        subtitle="Keep birthdays, anniversaries, and yearly milestones in a dedicated rhythm so the joyful dates stay visible too."
        action={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FilterListRounded />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {filterLabel}
          </Button>
        }
      />

      <StatStrip
        items={[
          { label: "Recurring", value: recurringDates.length, caption: "Saved yearly celebrations" },
          {
            label: "Priority",
            value: priorityCount,
            caption: "Marked as important",
            tint: "#f59e0b",
          },
          {
            label: "Next up",
            value: recurringDates[0]?.daysRemaining ?? "—",
            caption: recurringDates[0] ? `${recurringDates[0].title} in days` : "No celebration queued",
            tint: "#7c3aed",
          },
        ]}
      />

      {recurringDates.length === 0 ? (
        <EmptyState
          icon={<AutoAwesomeRounded sx={{ fontSize: 40, color: "#7c3aed" }} />}
          title="No recurring celebrations yet"
          description="Add birthdays, anniversaries, or yearly milestones to keep a softer, joyful side of your timeline visible."
          actionLabel="Add a celebration"
          actionTo="/add"
        />
      ) : (
        <Box>
          {renderGroup("Today", grouped.today)}
          {renderGroup("This Week", grouped.week)}
          {renderGroup("This Month", grouped.month)}
          {renderGroup("This Year", grouped.year)}
          {renderGroup("Later", grouped.later)}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 4,
            mt: 1,
          },
        }}
      >
        {filterSections.map((section) => (
          <Box key={section.title} sx={{ py: 0.4 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, color: "text.secondary", display: "block" }}
            >
              {section.title}
            </Typography>
            {section.options.map((option) => (
              <MenuItem
                key={option.value}
                selected={filter === option.value}
                onClick={() => {
                  setFilter(option.value);
                  setAnchorEl(null);
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                  <span>{option.label}</span>
                  {filter === option.value && (
                    <Chip label="Active" size="small" color="primary" />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>
    </PageShell>
  );
};

export default Celebrations;
